import { Copy, Download, ImageDown, Printer } from "lucide-react";
import { Link } from "@/lib/nav";
import { useState } from "react";
import { toast } from "sonner";

import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { Input } from "@buildpy/ui/input";
import { INTAKE_PRINT_PATH, intakeQrFileName, intakeQrPngFileName } from "@buildpy/core/modules/qr/intake-url";
import { QrImage } from "@/modules/qr/qr-image";
import { qrPngBlob } from "@/modules/qr/qr-png";

export interface IntakeLink {
  slug: string;
  url: string;
  svg: string;
}

// 간편접수 링크 — 절대 주소 · 복사 · QR 미리보기 · SVG/PNG 내려받기 · 인쇄용 보기(구성원 모두). 주소를 못 만들면 안내 한 줄.
const SettingsIntakeLink = ({ link }: { link: IntakeLink | null }) =>
  link ? (
    <IntakeLinkCard {...link} />
  ) : (
    <SectionCard className="text-sm" data-testid="settings-intake-link" padding="sm">
      <h2 className="mb-1 text-sm font-semibold">접수 링크</h2>
      <p className="text-xs text-muted-foreground" data-testid="settings-intake-link-missing">
        회사 주소 이름이 없어 접수 링크를 만들 수 없습니다.
      </p>
    </SectionCard>
  );

const IntakeLinkCard = ({ slug, url, svg }: IntakeLink) => {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("접수 주소를 복사했습니다");
    } catch {
      toast.error("복사하지 못했습니다");
    }
  };

  const save = (blob: Blob, fileName: string) => {
    const href = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = href;
    anchor.download = fileName;
    anchor.click();
    // click 직후 해제하면 일부 브라우저가 내려받기를 시작하기 전에 주소가 사라진다.
    setTimeout(() => URL.revokeObjectURL(href), 1000);
  };

  const download = () => save(new Blob([svg], { type: "image/svg+xml" }), intakeQrFileName(slug));

  // 래스터화 중에는 잠근다 — 두 번 눌러 파일이 두 개 내려오지 않게.
  const [pngPending, setPngPending] = useState(false);
  const downloadPng = async () => {
    if (pngPending) return;
    setPngPending(true);
    try {
      save(await qrPngBlob(svg), intakeQrPngFileName(slug));
    } catch {
      toast.error("PNG 를 만들지 못했습니다");
    } finally {
      setPngPending(false);
    }
  };

  return (
    <SectionCard className="text-sm" data-testid="settings-intake-link" padding="sm">
      <h2 className="mb-1 text-sm font-semibold">접수 링크</h2>
      <p className="mb-3 text-xs text-muted-foreground">
        홈페이지·명함·전단에 붙이면 고객이 바로 상담을 접수합니다.
      </p>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <Input
            data-testid="settings-intake-url"
            id="settings-intake-url"
            label="접수 주소"
            name="settings-intake-url"
            readOnly
            value={url}
          />
          <div className="flex flex-wrap gap-2">
            <Button data-testid="settings-intake-copy" size="sm" type="button" variant="outline" onClick={copy}>
              <Copy className="size-4" />
              주소 복사
            </Button>
            <Button data-testid="settings-intake-qr-download" size="sm" type="button" variant="outline" onClick={download}>
              <Download className="size-4" />
              QR 내려받기
            </Button>
            <Button
              data-testid="settings-intake-qr-png"
              disabled={pngPending}
              size="sm"
              type="button"
              variant="outline"
              onClick={downloadPng}>
              <ImageDown className="size-4" />
              PNG 내려받기
            </Button>
            <Button asChild data-testid="settings-intake-print" size="sm" variant="outline">
              <Link href={INTAKE_PRINT_PATH}>
                <Printer className="size-4" />
                인쇄용 보기
              </Link>
            </Button>
          </div>
        </div>
        <QrImage alt="간편접수 QR" className="size-32 shrink-0 self-center rounded-md border sm:self-start" svg={svg} />
      </div>
    </SectionCard>
  );
};

export default SettingsIntakeLink;
